import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Package, ShoppingBag, Star, TrendingUp, Clock, CheckCircle,
  Truck, Tag, Phone, MapPin, AlertCircle, Navigation, Banknote,
  CreditCard, X, ChevronDown, ChevronUp, Store, ToggleLeft, ToggleRight,
  Gift, Copy, Wallet, MessageCircle, Search, ArrowRight, Boxes, FileText, Send
} from 'lucide-react';
import Navbar from '../components/Navbar';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const STATUS_CONFIG = {
  pending:          { label: 'Pending',          color: '#F59E0B', bg: '#FFFBEB' },
  confirmed:        { label: 'Confirmed',         color: '#3B82F6', bg: '#EFF6FF' },
  processing:       { label: 'Processing',        color: '#8B5CF6', bg: '#F5F3FF' },
  out_for_delivery: { label: 'Out for Delivery',  color: 'var(--orange)', bg: 'var(--orange-soft)' },
  delivered:        { label: 'Delivered',         color: 'var(--green)', bg: '#ECFDF5' },
  cancelled:        { label: 'Cancelled',         color: 'var(--red)', bg: '#FEF2F2' },
};

const STATUS_FLOW = ['pending','confirmed','processing','out_for_delivery','delivered'];

function OwnerQuoteCard({ quote, onRespond }) {
  const [price, setPrice] = useState(quote.quotedPrice || '');
  const [days, setDays] = useState(quote.deliveryDays || '');
  const [message, setMessage] = useState(quote.ownerMessage || '');
  const [saving, setSaving] = useState(false);
  const respond = async (status) => {
    setSaving(true);
    try { await onRespond(quote._id, { status, quotedPrice: price, deliveryDays: days, ownerMessage: message }); }
    finally { setSaving(false); }
  };
  return (
    <article className={`owner-quote-card status-${quote.status}`}>
      <div className="owner-quote-head">
        <div><FileText size={17} /><div><strong>{quote.material?.name || 'Material'}</strong><span>#{quote._id.slice(-8).toUpperCase()}</span></div></div>
        <span>{quote.status}</span>
      </div>
      <div className="owner-quote-body">
        <div className="owner-quote-customer">
          <div><span>Customer</span><strong>{quote.customer?.name || 'Customer'}</strong><small>{quote.customer?.phone}</small></div>
          <div><span>Requirement</span><strong>{quote.quantity} {quote.unit}</strong><small>{quote.variant || 'Standard product'}</small></div>
          <div><span>Deliver to</span><strong>{quote.deliveryAddress?.city || 'Not set'}</strong><small>{quote.deliveryAddress?.pincode}</small></div>
        </div>
        {quote.customerNote && <p className="owner-quote-note">{quote.customerNote}</p>}
        {quote.status === 'pending' ? (
          <div className="owner-quote-response">
            <label>Total quoted price<input className="form-input" type="number" min="1" placeholder="Rs" value={price} onChange={e => setPrice(e.target.value)} /></label>
            <label>Delivery days<input className="form-input" type="number" min="0" placeholder="Days" value={days} onChange={e => setDays(e.target.value)} /></label>
            <label className="owner-quote-message">Message<input className="form-input" placeholder="Availability, terms, or delivery details" value={message} onChange={e => setMessage(e.target.value)} /></label>
            <div className="owner-quote-actions"><button className="btn-outline" disabled={saving} onClick={() => respond('rejected')}>Decline</button><button className="btn-primary" disabled={saving} onClick={() => respond('quoted')}><Send size={14} /> Send Quote</button></div>
          </div>
        ) : (
          <div className="owner-quote-result"><strong>{quote.status === 'accepted' ? `Accepted / Rs ${Number(quote.quotedPrice || 0).toLocaleString('en-IN')}` : quote.status === 'quoted' ? `Rs ${Number(quote.quotedPrice || 0).toLocaleString('en-IN')}` : quote.status === 'declined' ? 'Customer declined' : 'Request declined'}</strong>{quote.deliveryDays !== undefined && <span>{quote.deliveryDays} day delivery</span>}{quote.ownerMessage && <p>{quote.ownerMessage}</p>}</div>
        )}
      </div>
    </article>
  );
}

// ── Navigate to Customer (Google Maps) ───────────────────────────────────
function NavigateToCustomer({ order }) {
  const addr = order.deliveryAddress;
  const hasCoords = addr?.lat && addr?.lng;

  const openMaps = () => {
    let url;
    if (hasCoords) {
      url = `https://www.google.com/maps/dir/?api=1&destination=${addr.lat},${addr.lng}&travelmode=driving`;
    } else {
      const dest = encodeURIComponent([addr?.street, addr?.city, addr?.state, addr?.pincode].filter(Boolean).join(', '));
      url = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
    }
    window.open(url, '_blank');
  };

  return (
    <button onClick={openMaps}
      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#1A73E8,#0D62C9)', color: 'white', border: 'none', borderRadius: 9, padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-body)', flexShrink: 0 }}>
      <Navigation size={13} /> Navigate
    </button>
  );
}

// ── Payment Completion Modal ──────────────────────────────────────────────
function DeliveryCompleteModal({ order, onClose, onComplete }) {
  const [method, setMethod] = useState('cash');
  const [loading, setLoading] = useState(false);
  const remaining = order.remainingAmount || 0;

  const handleComplete = async () => {
    setLoading(true);
    try {
      if (remaining <= 0) {
        await API.put(`/orders/${order._id}/status`, { status: 'delivered' });
        toast.success('Delivery marked complete!');
        onComplete(order._id);
        onClose();
        return;
      }
      if (method === 'razorpay') {
        if (!window.Razorpay) {
          toast.error('Razorpay checkout is not loaded. Please refresh.');
          return;
        }
        // Open Razorpay for remaining amount
        const payRes = await API.post('/payment/create-remaining', {
          orderId: order._id,
          amount: remaining
        });
        if (payRes.data.nothingDue) {
          // test mode — auto complete
          await API.put(`/orders/${order._id}/status`, { status: 'delivered' });
          toast.success('Delivery marked complete!');
          onComplete(order._id);
          onClose();
          return;
        }
        if (payRes.data.testMode) {
          await API.post('/payment/verify-remaining', {
            orderId: order._id,
            razorpayOrderId: payRes.data.orderId
          });
          await API.put(`/orders/${order._id}/status`, { status: 'delivered' });
          toast.success('Delivery marked complete!');
          onComplete(order._id);
          onClose();
          return;
        }
        new window.Razorpay({
          key: payRes.data.key,
          amount: payRes.data.amount,
          currency: 'INR',
          name: payRes.data.merchantName || 'Material Stock Admin',
          description: `Balance for Order #${order._id.slice(-6).toUpperCase()}`,
          order_id: payRes.data.orderId,
          handler: async (response) => {
            await API.post('/payment/verify-remaining', {
              orderId: order._id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature
            });
            await API.put(`/orders/${order._id}/status`, { status: 'delivered' });
            toast.success('Payment received & delivery complete!');
            onComplete(order._id);
            onClose();
          },
          theme: { color: getComputedStyle(document.documentElement).getPropertyValue('--orange').trim() || '#15803D' }
        }).open();
      } else {
        // Record collection before delivery so an unpaid order cannot be completed.
        await API.put(`/orders/${order._id}/payment-complete`, { method: 'cash' });
        await API.put(`/orders/${order._id}/status`, { status: 'delivered' });
        toast.success('Delivery complete! Cash collected.');
        onComplete(order._id);
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to complete delivery');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, padding: '24px 20px 32px' }} className="fade-in">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontWeight: 700, fontSize: 17, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={18} color="var(--green)" /> Complete Delivery
          </h3>
          <button onClick={onClose} style={{ background: 'var(--gray-light)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', display: 'flex' }}>
            <X size={16} />
          </button>
        </div>

        {/* Order Summary */}
        <div style={{ background: 'var(--gray-light)', borderRadius: 12, padding: '12px 14px', marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
            Order #{order._id.slice(-8).toUpperCase()}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span style={{ color: 'var(--gray)' }}>Customer</span>
            <span style={{ fontWeight: 600 }}>{order.customer?.name}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span style={{ color: 'var(--gray)' }}>Total Order</span>
            <span style={{ fontWeight: 600 }}>₹{order.totalAmount?.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span style={{ color: 'var(--gray)' }}>Advance Paid</span>
            <span style={{ fontWeight: 600, color: 'var(--green)' }}>₹{order.advanceAmount} ✓</span>
          </div>
          <div style={{ borderTop: '1px dashed var(--gray-border)', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800 }}>
            <span>Amount to Collect</span>
            <span style={{ color: 'var(--orange)' }}>₹{remaining.toLocaleString()}</span>
          </div>
        </div>

        {/* Payment Method */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>How did customer pay?</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setMethod('cash')}
              style={{ flex: 1, padding: '12px', borderRadius: 12, border: `2px solid ${method === 'cash' ? 'var(--green)' : 'var(--gray-border)'}`, background: method === 'cash' ? '#ECFDF5' : 'white', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <Banknote size={22} color={method === 'cash' ? 'var(--green)' : 'var(--gray)'} />
              <span style={{ fontWeight: 700, fontSize: 13, color: method === 'cash' ? 'var(--green)' : 'var(--dark)' }}>Cash</span>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>Collected in hand</span>
            </button>
            <button onClick={() => setMethod('razorpay')}
              style={{ flex: 1, padding: '12px', borderRadius: 12, border: `2px solid ${method === 'razorpay' ? 'var(--orange)' : 'var(--gray-border)'}`, background: method === 'razorpay' ? 'var(--orange-soft)' : 'white', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <CreditCard size={22} color={method === 'razorpay' ? 'var(--orange)' : 'var(--gray)'} />
              <span style={{ fontWeight: 700, fontSize: 13, color: method === 'razorpay' ? 'var(--orange)' : 'var(--dark)' }}>Razorpay</span>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>Online payment</span>
            </button>
          </div>
        </div>

        <button onClick={handleComplete} disabled={loading}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'var(--green)', color: 'white', border: 'none', borderRadius: 12, padding: '15px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          <CheckCircle size={18} />
          {loading ? 'Processing...' : `Mark Delivered${remaining > 0 ? ` · ₹${remaining.toLocaleString()} Collected` : ''}`}
        </button>
      </div>
    </div>
  );
}

// ── Order Card ────────────────────────────────────────────────────────────
function OrderCard({ order, onStatusChange, onDeliveryComplete }) {
  const [expanded, setExpanded] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const currentIdx = STATUS_FLOW.indexOf(order.status);
  const isOutForDelivery = order.status === 'out_for_delivery';
  const isDelivered = order.status === 'delivered';
  const addr = order.deliveryAddress;
  const customerPhone = String(order.customer?.phone || '').replace(/\D/g, '');
  const whatsappPhone = customerPhone.length === 10 ? `91${customerPhone}` : customerPhone;
  const whatsappText = encodeURIComponent([
    `Hello ${order.customer?.name || 'Customer'},`,
    '',
    `This is your supplier regarding Material Stock order #${order._id.slice(-8).toUpperCase()}.`,
    `Current status: ${STATUS_CONFIG[order.status]?.label || order.status}`,
    '',
    'Items:',
    ...(order.items || []).map((item, index) => `${index + 1}. ${item.name}${item.variant ? ` (${item.variant})` : ''} - ${item.quantity} ${item.unit || ''}`),
    '',
    `Order total: Rs ${Number(order.totalAmount || 0).toLocaleString('en-IN')}`,
    `Balance on delivery: Rs ${Number(order.remainingAmount || 0).toLocaleString('en-IN')}`,
    `Delivery address: ${[addr?.street, addr?.city, addr?.state, addr?.pincode].filter(Boolean).join(', ') || 'Not available'}`,
    '',
    'Please reply here if you need to coordinate delivery.'
  ].join('\n'));

  const nextStatus = currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1
    ? STATUS_FLOW[currentIdx + 1]
    : null;

  const nextLabel = nextStatus ? STATUS_CONFIG[nextStatus]?.label : null;

  return (
    <>
      <div className={`owner-order-card status-${order.status}`} style={{ background: 'white', borderRadius: 14, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
        {/* Status Bar */}
        <div className="owner-order-status" style={{ padding: '10px 14px', background: status.bg, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: status.color }}>{status.label}</span>
            <span style={{ color: 'var(--gray)', fontSize: 11 }}>#{order._id.slice(-8).toUpperCase()}</span>
          </div>
          <span style={{ color: 'var(--gray)', fontSize: 11 }}>{new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
        </div>

        <div className="owner-order-body" style={{ padding: '12px 14px' }}>
          {/* Customer Info */}
          <div className="owner-order-customer" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '9px 12px', background: 'var(--gray-light)', borderRadius: 10 }}>
            <div style={{ width: 34, height: 34, background: 'linear-gradient(135deg,var(--orange),var(--amber))', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
              {order.customer?.name?.[0] || 'C'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{order.customer?.name}</div>
              <div style={{ color: 'var(--gray)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <a href={`tel:${order.customer?.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'var(--orange)', fontWeight: 600 }}>
                  <Phone size={10} /> {order.customer?.phone}
                </a>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <MapPin size={10} /> {[addr?.street, addr?.city, addr?.state].filter(Boolean).join(', ')}
                </span>
              </div>
            </div>
            <NavigateToCustomer order={order} />
            <a href={whatsappPhone ? `https://wa.me/${whatsappPhone}?text=${whatsappText}` : undefined}
              target="_blank" rel="noreferrer" title="WhatsApp customer" aria-label="WhatsApp customer"
              onClick={(event) => { if (!whatsappPhone) { event.preventDefault(); toast.error('Customer phone not available'); } }}
              style={{ width: 34, height: 34, borderRadius: 9, background: '#ECFDF5', color: 'var(--green)', border: '1px solid #A7F3D0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <MessageCircle size={15} />
            </a>
          </div>

          {/* Items */}
          <div className="owner-order-items" style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
            {order.items?.map((item, i) => (
              <span key={i} className="tag" style={{ fontSize: 11 }}>
                {item.name} × {item.quantity} {item.unit}
              </span>
            ))}
          </div>

          {/* Payment Status */}
          <div className="owner-order-payment" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 13 }}>
              <span style={{ color: 'var(--gray)' }}>Total: </span>
              <span style={{ fontWeight: 700 }}>₹{order.totalAmount?.toLocaleString()}</span>
              <span style={{ color: 'var(--gray)', marginLeft: 8 }}>Advance: </span>
              <span style={{ fontWeight: 700, color: order.paymentStatus?.advance === 'paid' ? 'var(--green)' : 'var(--red)' }}>
                {order.paymentStatus?.advance === 'paid' ? `₹${order.advanceAmount} ✓` : 'Pending'}
              </span>
            </div>
            {!isDelivered && (
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange)' }}>
                Balance: ₹{order.remainingAmount?.toLocaleString()}
              </span>
            )}
            {isDelivered && (
              <span className="badge badge-green" style={{ fontSize: 11 }}>✓ Fully Delivered</span>
            )}
          </div>

          {/* Action Buttons */}
          {!isDelivered && (
            <div className="owner-order-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* Navigate button (prominent when out for delivery) */}
              {isOutForDelivery && (
                <button onClick={() => {
                  const dest = addr?.lat && addr?.lng
                    ? `${addr.lat},${addr.lng}`
                    : encodeURIComponent([addr?.street, addr?.city, addr?.state, addr?.pincode].filter(Boolean).join(', '));
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`, '_blank');
                }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'linear-gradient(135deg,#1A73E8,#0D62C9)', color: 'white', border: 'none', borderRadius: 9, padding: '10px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)' }}>
                  <Navigation size={15} /> Open Navigation
                </button>
              )}

              {/* Next Status */}
              {nextStatus && !isOutForDelivery && (
                <button onClick={() => onStatusChange(order._id, nextStatus)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: STATUS_CONFIG[nextStatus]?.color, color: 'white', border: 'none', borderRadius: 9, padding: '10px', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-body)' }}>
                  → Mark {nextLabel}
                </button>
              )}

              {/* When out for delivery — show "Reached, Mark Delivered" */}
              {isOutForDelivery && (
                <button onClick={() => setShowCompleteModal(true)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'var(--green)', color: 'white', border: 'none', borderRadius: 9, padding: '10px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)' }}>
                  <CheckCircle size={15} /> Reached · Complete Delivery
                </button>
              )}

              {/* Cancel */}
              {order.paymentStatus?.advance !== 'paid' && order.status === 'pending' && <button onClick={() => onStatusChange(order._id, 'cancelled')}
                style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FEF2F2', border: '1.5px solid #FECACA', borderRadius: 9, cursor: 'pointer', color: 'var(--red)', flexShrink: 0 }}>
                <X size={14} />
              </button>}
            </div>
          )}

          {/* Expand Toggle */}
          <button onClick={() => setExpanded(!expanded)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: 12, marginTop: 8, padding: '4px 0' }}>
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {expanded ? 'Less details' : 'More details'}
          </button>

          {/* Expanded Details */}
          {expanded && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--gray-border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--gray)' }}>
                <span style={{ fontWeight: 600, color: 'var(--dark)' }}>Delivery Address: </span>
                {[addr?.street, addr?.city, addr?.state, addr?.pincode].filter(Boolean).join(', ')}
                {addr?.lat && addr?.lng && (
                  <span style={{ marginLeft: 6, color: 'var(--blue)', fontSize: 11 }}>
                    📍 {Number(addr.lat).toFixed(4)}, {Number(addr.lng).toFixed(4)}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: 'var(--gray)' }}>
                <span style={{ fontWeight: 600, color: 'var(--dark)' }}>Payment Method: </span>
                Advance via Razorpay · Balance via {order.paymentMethod?.remaining || 'cash'}
              </div>
              {order.notes && (
                <div style={{ fontSize: 12, color: 'var(--gray)' }}>
                  <span style={{ fontWeight: 600, color: 'var(--dark)' }}>Notes: </span>{order.notes}
                </div>
              )}
              {/* Status progress */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                {STATUS_FLOW.map((s, i) => (
                  <React.Fragment key={s}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', background: i <= currentIdx ? STATUS_CONFIG[s]?.color : 'var(--gray-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i < currentIdx && <CheckCircle size={12} color="white" />}
                        {i === currentIdx && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'white' }} />}
                      </div>
                      <span style={{ fontSize: 8, color: i <= currentIdx ? STATUS_CONFIG[s]?.color : 'var(--gray)', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {STATUS_CONFIG[s]?.label.split(' ')[0]}
                      </span>
                    </div>
                    {i < STATUS_FLOW.length - 1 && (
                      <div style={{ flex: 1, height: 2, background: i < currentIdx ? 'var(--green)' : 'var(--gray-border)', marginBottom: 14 }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {showCompleteModal && (
        <DeliveryCompleteModal
          order={order}
          onClose={() => setShowCompleteModal(false)}
          onComplete={onDeliveryComplete}
        />
      )}
    </>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────
export default function OwnerDashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(tabParam || 'orders');

  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [offerForm, setOfferForm] = useState({ title: '', discount: '', material: '' });

  useEffect(() => {
    Promise.all([
      API.get('/orders/owner-orders').catch(() => ({ data: [] })),
      API.get('/owners/profile').catch(() => ({ data: null })),
      API.get('/quotes/owner').catch(() => ({ data: [] }))
    ]).then(([ordersRes, profileRes, quotesRes]) => {
      setOrders(ordersRes.data || []);
      setProfile(profileRes.data || null);
      setQuotes(quotesRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const updateStatus = async (orderId, status) => {
    try {
      await API.put(`/orders/${orderId}/status`, { status });
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, status } : o));
      toast.success(`Order updated to ${STATUS_CONFIG[status]?.label}`);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update status'); }
  };

  const handleDeliveryComplete = (orderId) => {
    setOrders(prev => prev.map(o => o._id === orderId
      ? { ...o, status: 'delivered', paymentStatus: { ...o.paymentStatus, remaining: 'paid' } }
      : o
    ));
  };

  const toggleShop = async () => {
    try {
      const res = await API.put('/owners/profile', { isOpen: !profile.isOpen });
      setProfile(res.data);
      toast.success(res.data.isOpen ? 'Shop is now Open' : 'Shop is now Closed');
    } catch { toast.error('Failed to update shop status'); }
  };

  const addOffer = async () => {
    if (!offerForm.title || !offerForm.material) { toast.error('Fill offer title and material'); return; }
    try {
      await API.post('/owners/offers', offerForm);
      toast.success('Offer added!');
      setOfferForm({ title: '', discount: '', material: '' });
    } catch { toast.error('Failed to add offer'); }
  };

  const respondToQuote = async (quoteId, response) => {
    try {
      const res = await API.put(`/quotes/${quoteId}/respond`, response);
      setQuotes(current => current.map(quote => quote._id === quoteId ? { ...quote, ...res.data } : quote));
      toast.success(response.status === 'rejected' ? 'Quote declined' : 'Quote sent to customer');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to update quote');
      throw err;
    }
  };

  const totalRevenue = orders.filter(o => o.paymentStatus?.advance === 'paid').reduce((s, o) => s + (o.advanceAmount || 0), 0);
  const referralLink = `${window.location.origin}/register/owner?ref=${user?.referralCode || ''}`;
  const referralCredits = user?.referralCredits || [];
  const pendingReferralAmount = referralCredits
    .filter(ref => (ref.payoutStatus || 'pending') === 'pending')
    .reduce((sum, ref) => sum + Number(ref.amount || 0), 0);
  const paidReferralAmount = referralCredits
    .filter(ref => ref.payoutStatus === 'paid')
    .reduce((sum, ref) => sum + Number(ref.amount || 0), 0);
  const formatMoney = (value) => `Rs ${Number(value || 0).toLocaleString('en-IN')}`;
  const stats = [
    { label: 'Total Orders', value: orders.length, icon: ShoppingBag, color: '#3B82F6' },
    { label: 'Pending', value: orders.filter(o => o.status === 'pending').length, icon: Clock, color: '#F59E0B' },
    { label: 'Delivered', value: orders.filter(o => o.status === 'delivered').length, icon: CheckCircle, color: 'var(--green)' },
    { label: 'Revenue', value: formatMoney(totalRevenue), icon: TrendingUp, color: 'var(--orange)' },
    { label: 'Wallet', value: formatMoney(user?.walletBalance), icon: Wallet, color: 'var(--green)' },
  ];

  const copyReferral = async () => {
    await navigator.clipboard.writeText(referralLink);
    toast.success('Supplier referral link copied');
  };

  const cleanReferralProgramCard = (
    <div className="owner-referral-card" style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: 'var(--shadow)', marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <h3 style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Gift size={16} color="var(--orange)" /> Referral Program
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--green)', fontWeight: 800 }}>
          <Wallet size={15} /> Wallet: {formatMoney(user?.walletBalance)}
        </div>
      </div>

      <div className="owner-referral-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 12 }}>
        <div className="owner-referral-metric" style={{ background: 'var(--orange-soft)', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, color: 'var(--orange)', fontWeight: 800, marginBottom: 3 }}>YOUR CODE</div>
          <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: 1 }}>{user?.referralCode || 'Loading'}</div>
        </div>
        <div className="owner-referral-metric" style={{ background: '#ECFDF5', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, color: 'var(--green)', fontWeight: 800, marginBottom: 3 }}>TOTAL EARNED</div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>{formatMoney(user?.referralStats?.totalEarnings)}</div>
        </div>
        <div className="owner-referral-metric" style={{ background: '#FFFBEB', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, color: '#D97706', fontWeight: 800, marginBottom: 3 }}>PENDING PAYOUT</div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>{formatMoney(pendingReferralAmount)}</div>
        </div>
        <div className="owner-referral-metric" style={{ background: '#EFF6FF', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, color: '#2563EB', fontWeight: 800, marginBottom: 3 }}>PAID</div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>{formatMoney(paidReferralAmount)}</div>
        </div>
      </div>

      <div className="owner-referral-summary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12, padding: '9px 12px', background: 'var(--gray-light)', borderRadius: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--gray)' }}>
          <strong style={{ color: 'var(--dark)' }}>{user?.referralStats?.totalReferrals || referralCredits.length || 0}</strong> total referrals
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray)' }}>
          Customer: <strong style={{ color: 'var(--dark)' }}>{user?.referralStats?.customerReferrals || 0}</strong>
          <span style={{ margin: '0 7px' }}>·</span>
          Owner: <strong style={{ color: 'var(--dark)' }}>{user?.referralStats?.ownerReferrals || 0}</strong>
        </div>
      </div>

      <button onClick={copyReferral} className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '11px' }}>
        <Copy size={14} /> Copy Supplier Referral Link
      </button>
    </div>
  );

  const matchesOrderSearch = (order) => {
    const q = orderSearch.trim().toLowerCase();
    if (!q) return true;
    const addr = order.deliveryAddress || {};
    const haystack = [
      order._id,
      order._id?.slice(-8),
      order.customer?.name,
      order.customer?.phone,
      order.customer?.email,
      order.status,
      addr.street,
      addr.city,
      addr.state,
      addr.pincode,
      order.totalAmount,
      order.remainingAmount,
      ...(order.items || []).flatMap(item => [item.name, item.category, item.variant, item.unit]),
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  };

  const filteredOrders = (statusFilter === 'all' ? orders : orders.filter(o => o.status === statusFilter)).filter(matchesOrderSearch);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }} className="page-with-bottom-nav">
      <Navbar />
      <div className="owner-dashboard-shell" style={{ maxWidth: 900, margin: '0 auto', padding: '16px 16px' }}>

        {/* Header */}
        <div className="owner-dashboard-hero" style={{ background: 'linear-gradient(135deg,var(--orange),var(--amber))', borderRadius: 16, padding: '18px 18px', marginBottom: 18, color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div className="owner-hero-copy">
              <span className="owner-hero-eyebrow">Supplier workspace</span>
              <h1 style={{ fontSize: 19, fontWeight: 700, marginBottom: 3 }}>Welcome, {user?.name?.split(' ')[0]}</h1>
              <div style={{ opacity: 0.85, fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Store size={12} /> {profile?.shopName}
                <span style={{ margin: '0 4px' }}>·</span>
                <MapPin size={12} /> {profile?.location?.city}
              </div>
              {profile?.verificationStatus !== 'approved' && (
                <div style={{ marginTop: 8, background: 'rgba(255,255,255,0.2)', borderRadius: 7, padding: '5px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5, width: 'fit-content' }}>
                  <AlertCircle size={11} /> Verification {profile?.verificationStatus || 'pending'}
                </div>
              )}
            </div>
            {/* Shop Open/Close Toggle */}
            <button className={`owner-shop-toggle ${profile?.isOpen ? 'open' : 'closed'}`} onClick={toggleShop}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 10, padding: '8px 14px', color: 'white', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'var(--font-body)' }}>
              {profile?.isOpen
                ? <><ToggleRight size={18} /> Shop Open</>
                : <><ToggleLeft size={18} /> Shop Closed</>}
            </button>
          </div>
          <div className="owner-hero-actions">
            <Link to="/owner/materials"><Boxes size={15} /> Manage Materials</Link>
            {profile?._id && <Link to={`/shops/${profile._id}`}>View Public Shop <ArrowRight size={15} /></Link>}
            {orders.filter(order => order.status === 'pending').length > 0 && (
              <span><Clock size={14} /> {orders.filter(order => order.status === 'pending').length} orders need attention</span>
            )}
          </div>
        </div>

        {/* Stats: 2x2 on mobile */}
        <div className="owner-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 18 }}>
          {stats.map((s, i) => (
            <div key={i} className="owner-stat-card" style={{ background: 'white', borderRadius: 12, padding: '14px 14px', boxShadow: 'var(--shadow)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 38, height: 38, background: s.color + '18', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <s.icon size={18} color={s.color} />
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'var(--font-main)', color: 'var(--dark)' }}>
                  {s.label === 'Revenue' ? formatMoney(totalRevenue) : s.label === 'Wallet' ? formatMoney(user?.walletBalance) : s.value}
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray)' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="owner-tabs" style={{ display: 'flex', background: 'white', borderRadius: 12, padding: 5, marginBottom: 16, gap: 4, boxShadow: 'var(--shadow)' }}>
          {[['orders','Orders'], ['quotes',`Quotes${quotes.filter(q => q.status === 'pending').length ? ` (${quotes.filter(q => q.status === 'pending').length})` : ''}`], ['offers','Offers'], ['profile','Profile']].map(([t, l]) => (
            <button key={t} onClick={() => setActiveTab(t)}
              style={{ flex: 1, padding: '9px 6px', borderRadius: 8, border: 'none', background: activeTab === t ? 'var(--orange)' : 'transparent', color: activeTab === t ? 'white' : 'var(--gray)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              {l}
            </button>
          ))}
        </div>

        {/* ── ORDERS TAB ── */}
        {activeTab === 'orders' && (
          <div>
            {/* Status Filter — scrollable pills */}
            <div className="owner-order-search" style={{ position: 'relative', marginBottom: 12 }}>
              <Search size={17} />
              <input
                className="form-input"
                placeholder="Search order number, customer name, phone, item, city, status..."
                value={orderSearch}
                onChange={e => setOrderSearch(e.target.value)}
                style={{ paddingLeft: 42, paddingRight: orderSearch ? 42 : 14, background: 'white' }}
              />
              {orderSearch && (
                <button onClick={() => setOrderSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 28, height: 28, borderRadius: 8, background: 'var(--gray-light)', color: 'var(--gray)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="owner-status-pills" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 14 }}>
              {[['all','All'], ...Object.entries(STATUS_CONFIG).map(([k,v]) => [k, v.label])].map(([k, l]) => (
                <button key={k} onClick={() => setStatusFilter(k)}
                  style={{ padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${statusFilter === k ? 'var(--orange)' : 'var(--gray-border)'}`, background: statusFilter === k ? 'var(--orange-soft)' : 'white', color: statusFilter === k ? 'var(--orange)' : 'var(--dark)', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {l} {k !== 'all' && `(${orders.filter(o => o.status === k).length})`}
                </button>
              ))}
            </div>

            {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>
              : filteredOrders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px 20px', background: 'white', borderRadius: 14 }}>
                  <Package size={48} color="var(--gray-border)" style={{ marginBottom: 12 }} />
                  <div style={{ fontWeight: 700, fontSize: 16 }}>No orders {statusFilter !== 'all' ? `with status "${STATUS_CONFIG[statusFilter]?.label}"` : 'yet'}</div>
                </div>
              ) : (
                <div className="owner-order-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {filteredOrders.map(order => (
                    <OrderCard key={order._id} order={order}
                      onStatusChange={updateStatus}
                      onDeliveryComplete={handleDeliveryComplete} />
                  ))}
                </div>
              )}
          </div>
        )}

        {/* ── OFFERS TAB ── */}
        {activeTab === 'quotes' && (
          <div className="owner-quote-list">
            {quotes.length === 0 ? (
              <div className="owner-quote-empty"><FileText size={34} /><strong>No quote requests yet</strong><span>Customer requests will appear here.</span></div>
            ) : quotes.map(quote => <OwnerQuoteCard key={quote._id} quote={quote} onRespond={respondToQuote} />)}
          </div>
        )}

        {activeTab === 'offers' && (
          <div className="owner-tab-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="owner-panel-card" style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
              <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Add New Offer</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Offer Title</label>
                  <input className="form-input" placeholder="e.g. Summer Sand Sale" value={offerForm.title} onChange={e => setOfferForm(f => ({ ...f, title: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div className="form-group" style={{ flex: 1, margin: 0 }}>
                    <label className="form-label">Discount %</label>
                    <input className="form-input" type="number" placeholder="e.g. 15" value={offerForm.discount} onChange={e => setOfferForm(f => ({ ...f, discount: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ flex: 1, margin: 0 }}>
                    <label className="form-label">Material</label>
                    <select className="form-input" value={offerForm.material} onChange={e => setOfferForm(f => ({ ...f, material: e.target.value }))}>
                      <option value="">Select</option>
                      {['sand','cement','iron','bricks','long_bricks','short_bricks'].map(m => <option key={m} value={m}>{m.replace('_',' ')}</option>)}
                    </select>
                  </div>
                </div>
                <button onClick={addOffer} className="btn-primary" style={{ justifyContent: 'center', padding: '11px' }}>
                  <Tag size={14} /> Add Offer
                </button>
              </div>
            </div>

            {/* Current Offers */}
            {profile?.offers?.length > 0 && (
              <div className="owner-panel-card" style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
                <h4 style={{ fontWeight: 700, marginBottom: 12 }}>Current Offers</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {profile.offers.map((o, i) => (
                    <div key={i} style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 13 }}>🏷️ {o.title}</span>
                        {o.discount > 0 && <span style={{ color: 'var(--gray)', fontSize: 12, marginLeft: 8 }}>{o.discount}% off</span>}
                      </div>
                      <span className="badge badge-orange" style={{ fontSize: 10 }}>{o.material}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── PROFILE TAB ── */}
        {activeTab === 'profile' && profile && (
          <div className="owner-panel-card" style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Shop Profile</h3>
            <div className="owner-profile-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
              {[
                ['Shop Name', profile.shopName],
                ['City', profile.location?.city],
                ['State', profile.location?.state],
                ['Pincode', profile.location?.pincode],
                ['Verification', profile.verificationStatus],
                ['Rating', `${profile.rating?.toFixed(1) || '0'} ★ (${profile.totalRatings || 0} reviews)`],
                ['Latitude', profile.location?.lat || '—'],
                ['Longitude', profile.location?.lng || '—'],
              ].map(([label, value]) => (
                <div key={label} style={{ padding: '10px 12px', background: 'var(--gray-light)', borderRadius: 9 }}>
                  <div style={{ fontSize: 10, color: 'var(--gray)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{value || '—'}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--gray-light)', borderRadius: 9 }}>
              <div style={{ fontSize: 10, color: 'var(--gray)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>Full Address</div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{profile.location?.address}, {profile.location?.city}, {profile.location?.state} – {profile.location?.pincode}</div>
            </div>
            {profile.description && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--gray-light)', borderRadius: 9 }}>
                <div style={{ fontSize: 10, color: 'var(--gray)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>Description</div>
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>{profile.description}</div>
              </div>
            )}
          </div>
        )}

        {cleanReferralProgramCard}
      </div>
    </div>
  );
}
